import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, Clock, Star, SortAsc, SortDesc, Filter, AlertTriangle, Activity, ChevronRight, X, Sparkles, ShieldAlert, ShieldCheck, Eye } from 'lucide-react';
import { authenticatedFetch } from '../utils/api';
import { useNavigate } from 'react-router-dom';
import { useTeam } from '../contexts/TeamContext';
import { ResponsiveContainer, AreaChart, Area } from 'recharts';
import Loading from './Loading';
import Modal from './Modal';
import RunnerStatus from './RunnerStatus';

// Item 7: Mini sparkline for execution trend
const MiniSparkline = ({ data }) => {
  if (!data || data.length < 2) return null;

  return (
    <div className="w-[72px] h-[24px]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 2, left: 0 }}>
          <Area
            type="monotone"
            dataKey="c"
            stroke="#FF6B6B"
            fill="#FF6B6B"
            fillOpacity={0.1}
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

// Item 2: Priority breakdown bar
const PriorityBar = ({ data }) => {
  if (!data) return null;
  const { p1, p2, p3, p4, p5 } = data;
  const total = p1 + p2 + p3 + p4 + p5;
  if (total === 0) return null;

  const segments = [
    { count: p1, color: 'bg-danger', label: 'Critical' },
    { count: p2, color: 'bg-accent', label: 'High' },
    { count: p3, color: 'bg-warning', label: 'Medium' },
    { count: p4, color: 'bg-info', label: 'Low' },
    { count: p5, color: 'bg-ink-muted', label: 'Info' },
  ].filter(s => s.count > 0);

  return (
    <div className="flex items-center gap-2 mt-1.5">
      <div className="flex h-1.5 rounded-full overflow-hidden flex-1 max-w-[100px] bg-surface-tertiary">
        {segments.map((seg, i) => (
          <div
            key={i}
            className={`${seg.color} h-full`}
            style={{ width: `${(seg.count / total) * 100}%` }}
            title={`${seg.label}: ${seg.count}`}
          />
        ))}
      </div>
      <span className="text-tiny text-ink-tertiary">{total} triaged</span>
    </div>
  );
};

// AI risk breakdown for new findings since last visit
const NewFindingsRisk = ({ data }) => {
  if (!data || data.total === 0) return null;

  const { high, medium, low, pending, total } = data;
  const analyzed = total - (pending || 0);
  const hasHighRisk = high > 0;
  const hasMediumRisk = medium > 0;

  if (analyzed === 0 && pending > 0) {
    return (
      <div className="flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded bg-surface-tertiary border border-border/50">
        <Sparkles size={11} className="text-ink-muted" />
        <span className="text-tiny text-ink-tertiary">{pending} new · AI pending</span>
      </div>
    );
  }

  if (analyzed === 0) return null;

  const allLow = high === 0 && medium === 0 && low > 0;

  return (
    <div className={`flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded ${
      hasHighRisk
        ? 'bg-danger/8 border border-danger/20'
        : hasMediumRisk
          ? 'bg-warning/8 border border-warning/20'
          : 'bg-success/8 border border-success/20'
    }`}>
      <Sparkles size={11} className={
        hasHighRisk ? 'text-danger' : hasMediumRisk ? 'text-warning' : 'text-success'
      } />
      <div className="flex items-center gap-1 text-tiny">
        {hasHighRisk ? (
          <>
            <ShieldAlert size={11} className="text-danger" />
            <span className="font-semibold text-danger">{high} high risk</span>
            {medium > 0 && <span className="text-ink-tertiary">· {medium} med</span>}
            {low > 0 && <span className="text-ink-muted">· {low} low</span>}
          </>
        ) : hasMediumRisk ? (
          <>
            <span className="font-medium text-warning">{medium} medium risk</span>
            {low > 0 && <span className="text-ink-muted">· {low} low</span>}
          </>
        ) : allLow ? (
          <>
            <ShieldCheck size={11} className="text-success" />
            <span className="text-success font-medium">All low risk</span>
            <span className="text-ink-muted">({low})</span>
          </>
        ) : null}
        {pending > 0 && <span className="text-ink-muted">· {pending} pending</span>}
      </div>
    </div>
  );
};

// Item 4: Status filter options
const STATUS_FILTERS = [
  { key: 'running', label: 'Running', dot: 'bg-success' },
  { key: 'completed', label: 'Completed', dot: 'bg-success' },
  { key: 'stopped', label: 'Stopped', dot: 'bg-ink-muted' },
  { key: 'error', label: 'Error', dot: 'bg-danger' },
];

const SelectRunnerModal = ({ onClose }) => {
  const [runners, setRunners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const { currentTeam } = useTeam();
  const [sortOrder, setSortOrder] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showOnlyFavorites, setShowOnlyFavorites] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null);
  const [modalData, setModalData] = useState({});
  const [favorites, setFavorites] = useState(() => {
    return JSON.parse(localStorage.getItem('favoriteRunners') || '[]');
  });

  // Item 1: Recent runners from localStorage
  const [recentRunnerIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem('recentRunners') || '[]'); }
    catch (e) { return []; }
  });

  // Item 3: Last visited data from localStorage
  const [lastVisited, setLastVisited] = useState(() => {
    try { return JSON.parse(localStorage.getItem('runnerLastVisited') || '{}'); }
    catch (e) { return {}; }
  });

  // Item 5: Fix close - don't navigate away from current page
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleClose]);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const runnersUrl = currentTeam
          ? `/runners?team_id=${currentTeam.team_id}`
          : '/runners?personal=true';
        const visitedParam = encodeURIComponent(JSON.stringify(lastVisited));
        const modalUrl = currentTeam
          ? `/runners/modal-data?team_id=${currentTeam.team_id}&last_visited=${visitedParam}`
          : `/runners/modal-data?personal=true&last_visited=${visitedParam}`;

        const [runnersData, modalResult] = await Promise.all([
          authenticatedFetch(runnersUrl),
          authenticatedFetch(modalUrl).catch(() => ({}))
        ]);

        const sortedRunners = runnersData.sort((a, b) =>
          new Date(b.lastrun_at || 0) - new Date(a.lastrun_at || 0)
        );
        setRunners(sortedRunners);
        setModalData(modalResult);
      } catch (error) {
        console.error('Error fetching runners:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [currentTeam]);

  // Item 4: Filter by status — exclude runners without executions from main list
  const filteredRunners = runners.filter(runner => {
    const hasResults = parseInt(runner.total_executions || 0) > 0;
    if (!hasResults) return false;
    const matchesSearch = runner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (runner.domain && runner.domain.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesFavorites = !showOnlyFavorites || favorites.includes(runner.runner_id);
    const matchesStatus = !statusFilter || (runner.current_status || 'stopped') === statusFilter;
    return matchesSearch && matchesFavorites && matchesStatus;
  });

  const emptyRunners = runners.filter(r => parseInt(r.total_executions || 0) === 0);

  // Item 3: Check if runner has new results since last visit
  const hasNewResults = useCallback((runner) => {
    const visit = lastVisited[runner.runner_id];
    if (!visit || !runner.lastrun_at) return false;
    return new Date(runner.lastrun_at) > new Date(visit.at);
  }, [lastVisited]);

  // Item 3: Count new findings since last visit
  const getNewFindingsCount = useCallback((runner) => {
    const visit = lastVisited[runner.runner_id];
    if (!visit) return 0;
    const current = parseInt(runner.total_findings || 0);
    const previous = parseInt(visit.findings || 0);
    return Math.max(0, current - previous);
  }, [lastVisited]);

  const getRelativeTimeString = (date) => {
    if (!date) return 'Never';
    const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });
    const diff = (new Date(date) - new Date()) / (1000 * 60 * 60 * 24);
    if (Math.abs(diff) < 1) return 'Today';
    if (Math.abs(diff) < 2) return 'Yesterday';
    return rtf.format(Math.round(diff), 'day');
  };

  const getSortedRunners = useCallback(() => {
    return [...filteredRunners].sort((a, b) => {
      if (sortOrder === 'date') {
        const dateA = new Date(a.lastrun_at || 0);
        const dateB = new Date(b.lastrun_at || 0);
        return sortDirection === 'desc' ? dateB - dateA : dateA - dateB;
      }
      if (sortOrder === 'name') {
        return sortDirection === 'desc'
          ? b.name.localeCompare(a.name)
          : a.name.localeCompare(b.name);
      }
      if (sortOrder === 'findings') {
        const findingsA = parseInt(a.total_findings || 0);
        const findingsB = parseInt(b.total_findings || 0);
        return sortDirection === 'desc' ? findingsB - findingsA : findingsA - findingsB;
      }
      return 0;
    });
  }, [filteredRunners, sortOrder, sortDirection]);

  const toggleFavorite = (e, runnerId) => {
    e.stopPropagation();
    const newFavorites = favorites.includes(runnerId)
      ? favorites.filter(id => id !== runnerId)
      : [...favorites, runnerId];
    setFavorites(newFavorites);
    localStorage.setItem('favoriteRunners', JSON.stringify(newFavorites));
  };

  // Mark a runner as seen without navigating
  const markAsSeen = (e, runner) => {
    e.stopPropagation();
    const visited = { ...lastVisited };
    visited[runner.runner_id] = {
      at: new Date().toISOString(),
      findings: parseInt(runner.total_findings || 0)
    };
    setLastVisited(visited);
    localStorage.setItem('runnerLastVisited', JSON.stringify(visited));
  };

  // Items 1 & 3: Save recent runner + visit timestamp on selection
  const handleSelect = (runner) => {
    // Item 1: Update recent runners list
    const recent = JSON.parse(localStorage.getItem('recentRunners') || '[]');
    const updated = [runner.runner_id, ...recent.filter(id => id !== runner.runner_id)].slice(0, 3);
    localStorage.setItem('recentRunners', JSON.stringify(updated));

    // Item 3: Save visit timestamp and current findings count
    const visited = JSON.parse(localStorage.getItem('runnerLastVisited') || '{}');
    visited[runner.runner_id] = {
      at: new Date().toISOString(),
      findings: parseInt(runner.total_findings || 0)
    };
    localStorage.setItem('runnerLastVisited', JSON.stringify(visited));

    navigate(`/results/${runner.runner_id}`);
    onClose();
  };

  // Item 1: Resolve recent runner objects from loaded data
  const recentRunners = useMemo(() => {
    return recentRunnerIds
      .map(id => runners.find(r => r.runner_id === id))
      .filter(r => r && parseInt(r.total_executions || 0) > 0);
  }, [recentRunnerIds, runners]);

  // Item 4: Count runners per status for filter chips (only runners with executions)
  const statusCounts = useMemo(() => {
    const counts = {};
    runners.forEach(r => {
      if (parseInt(r.total_executions || 0) === 0) return;
      const status = r.current_status || 'stopped';
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [runners]);

  const hasActiveFilters = searchTerm || showOnlyFavorites || statusFilter;

  return (
    <Modal
      isOpen={true}
      onClose={handleClose}
      title="View Results"
      size="xl"
    >
      <div className="space-y-4">
        {/* Item 1: Recent runners quick access */}
        {!loading && recentRunners.length > 0 && !hasActiveFilters && (
          <div>
            <p className="text-tiny text-ink-tertiary font-medium uppercase tracking-wider mb-2">Recent</p>
            <div className="flex flex-wrap gap-2">
              {recentRunners.map(runner => {
                const isNew = hasNewResults(runner);
                const newCount = getNewFindingsCount(runner);
                return (
                  <button
                    key={`recent-${runner.runner_id}`}
                    onClick={() => handleSelect(runner)}
                    className="flex items-center gap-2 px-3 py-2 bg-surface-secondary border border-border rounded-md hover:border-accent/30 hover:shadow-card-hover transition-base"
                  >
                    {runner.domain && (
                      <img
                        src={`https://www.google.com/s2/favicons?domain=${runner.domain}&sz=16`}
                        alt=""
                        className="w-4 h-4 rounded-sm"
                        loading="lazy"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <span className="text-caption font-medium text-ink">{runner.name}</span>
                    {isNew && (
                      <span className="px-1.5 py-0.5 bg-accent text-white text-tiny font-medium rounded">
                        {newCount > 0 ? `+${newCount}` : 'New'}
                      </span>
                    )}
                    <ChevronRight size={14} className="text-ink-tertiary" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-tertiary" />
            <input
              type="text"
              placeholder="Search by name or domain..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2.5 bg-surface border border-border rounded-md text-body text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-ink-tertiary hover:text-ink hover:bg-surface-tertiary rounded transition-base"
                title="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>

          <button
            onClick={() => setShowOnlyFavorites(!showOnlyFavorites)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-md border transition-base ${
              showOnlyFavorites
                ? 'bg-warning-soft border-warning/30 text-warning'
                : 'border-border text-ink-secondary hover:bg-surface-tertiary'
            }`}
          >
            <Star size={16} className={showOnlyFavorites ? 'fill-warning' : ''} />
            <span className="text-caption">Favorites</span>
          </button>
        </div>

        {/* Item 4: Status filter chips */}
        {!loading && (
          <div className="flex flex-wrap items-center gap-2">
            {STATUS_FILTERS.map(({ key, label, dot }) => {
              const count = statusCounts[key] || 0;
              if (count === 0) return null;
              const isActive = statusFilter === key;
              return (
                <button
                  key={key}
                  onClick={() => setStatusFilter(isActive ? null : key)}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-tiny font-medium transition-base ${
                    isActive
                      ? 'bg-accent/10 border-accent/30 text-accent'
                      : 'border-border text-ink-secondary hover:bg-surface-tertiary'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                  <span>{label}</span>
                  <span className="text-ink-tertiary">{count}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Sort Controls */}
        <div className="flex flex-wrap items-center gap-2 p-2 bg-surface-tertiary rounded-md">
          <div className="flex items-center gap-1 text-tiny text-ink-tertiary">
            <Filter size={12} />
            <span>Sort:</span>
          </div>

          {[
            { key: 'date', icon: Clock, label: 'Date' },
            { key: 'name', icon: null, label: 'Name' },
            { key: 'findings', icon: null, label: 'Findings' }
          ].map(({ key, icon: Icon, label }) => (
            <button
              key={key}
              onClick={() => {
                setSortOrder(key);
                setSortDirection(prev => sortOrder === key ? (prev === 'desc' ? 'asc' : 'desc') : 'desc');
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-tiny font-medium transition-base ${
                sortOrder === key
                  ? 'bg-accent text-white'
                  : 'text-ink-secondary hover:bg-surface-secondary'
              }`}
            >
              {Icon && <Icon size={14} />}
              <span>{label}</span>
              {sortOrder === key && (
                sortDirection === 'desc' ? <SortDesc size={14} /> : <SortAsc size={14} />
              )}
            </button>
          ))}
        </div>

        {/* Results Count */}
        {!loading && (
          <p className="text-caption text-ink-tertiary">
            {filteredRunners.length} runner{filteredRunners.length !== 1 ? 's' : ''} with results
            {showOnlyFavorites && ' (favorites only)'}
            {statusFilter && ` · ${statusFilter}`}
            {emptyRunners.length > 0 && !hasActiveFilters && (
              <span className="text-ink-muted"> · {emptyRunners.length} without results</span>
            )}
          </p>
        )}

        {/* Runners List */}
        <div className="max-h-[50vh] overflow-y-auto space-y-2">
          {loading ? (
            <Loading size="md" variant="spinner" />
          ) : getSortedRunners().length === 0 ? (
            <div className="text-center py-12">
              <AlertTriangle size={32} className="mx-auto text-ink-muted mb-3" />
              <p className="text-body text-ink-secondary">No runners found</p>
              {hasActiveFilters && (
                <button
                  onClick={() => { setSearchTerm(''); setShowOnlyFavorites(false); setStatusFilter(null); }}
                  className="mt-2 text-caption text-accent hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            getSortedRunners().map(runner => {
              const totalFindings = parseInt(runner.total_findings || 0);
              const hiddenFindings = parseInt(runner.hidden_findings || 0);
              const visibleFindings = totalFindings - hiddenFindings;
              const lastRunFindings = parseInt(runner.results_count || 0);
              const isFavorite = favorites.includes(runner.runner_id);
              const isNew = hasNewResults(runner);
              const newCount = getNewFindingsCount(runner);
              const runnerModalData = modalData[runner.runner_id];

              return (
                <button
                  key={runner.runner_id}
                  onClick={() => handleSelect(runner)}
                  className={`w-full text-left p-4 rounded-md border transition-base bg-surface-secondary border-border hover:border-accent/30 hover:shadow-card-hover cursor-pointer ${isNew ? 'ring-1 ring-accent/20' : ''}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-body font-medium truncate text-ink">
                          {runner.name}
                        </h3>
                        {runner.domain && (
                          <span className="flex items-center gap-1.5 px-2 py-0.5 bg-surface-tertiary rounded text-tiny text-ink-tertiary truncate max-w-[180px]">
                            <img
                              src={`https://www.google.com/s2/favicons?domain=${runner.domain}&sz=16`}
                              alt=""
                              className="w-4 h-4 rounded-sm shrink-0"
                              loading="lazy"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            {runner.domain}
                          </span>
                        )}
                        <RunnerStatus status={runner.current_status || 'stopped'} />
                        {isNew && (
                          <span className="flex items-center gap-1 px-1.5 py-0.5 bg-accent text-white text-tiny font-medium rounded">
                            {newCount > 0 ? `+${newCount} new` : 'New'}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-4 text-caption text-ink-secondary">
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {getRelativeTimeString(runner.lastrun_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Activity size={14} />
                          {runner.total_executions || 0} runs
                        </span>
                        <span className="flex items-center gap-1">
                          <span className={lastRunFindings > 0 ? 'text-accent font-medium' : ''}>
                            {lastRunFindings} {lastRunFindings === 1 ? 'finding' : 'findings'}
                          </span>
                          <span className="text-ink-tertiary">
                            (last run) · {visibleFindings} total{hiddenFindings > 0 && (
                              <span className="text-ink-muted"> · {hiddenFindings} hidden</span>
                            )}
                          </span>
                        </span>
                      </div>

                      {runnerModalData?.priority && <PriorityBar data={runnerModalData.priority} />}
                      {isNew && runnerModalData?.newSinceVisit && <NewFindingsRisk data={runnerModalData.newSinceVisit} />}
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      {runnerModalData?.trend && (
                        <MiniSparkline data={runnerModalData.trend} />
                      )}
                      {isNew && (
                        <button
                          onClick={(e) => markAsSeen(e, runner)}
                          className="p-1.5 rounded hover:bg-surface-tertiary transition-base"
                          title="Mark as seen"
                        >
                          <Eye size={16} className="text-ink-muted hover:text-ink-secondary" />
                        </button>
                      )}
                      <button
                        onClick={(e) => toggleFavorite(e, runner.runner_id)}
                        className="p-1.5 rounded hover:bg-surface-tertiary transition-base"
                      >
                        <Star
                          size={16}
                          className={isFavorite ? 'fill-warning text-warning' : 'text-ink-muted hover:text-ink-secondary'}
                        />
                      </button>
                      <ChevronRight size={18} className="text-ink-tertiary" />
                    </div>
                  </div>
                </button>
              );
            })
          )}

          {/* Empty runners — collapsed by default */}
          {emptyRunners.length > 0 && !hasActiveFilters && (
            <details className="group mt-4">
              <summary className="flex items-center gap-2 py-2 px-3 text-caption text-ink-tertiary cursor-pointer hover:text-ink-secondary transition-base select-none list-none">
                <ChevronRight size={14} className="group-open:rotate-90 transition-transform" />
                {emptyRunners.length} runner{emptyRunners.length !== 1 ? 's' : ''} without results
              </summary>
              <div className="space-y-1.5 mt-2">
                {emptyRunners.map(runner => (
                  <div
                    key={runner.runner_id}
                    className="flex items-center gap-3 px-4 py-2.5 rounded-md border border-border/50 bg-surface-tertiary opacity-60"
                  >
                    {runner.domain && (
                      <img
                        src={`https://www.google.com/s2/favicons?domain=${runner.domain}&sz=16`}
                        alt=""
                        className="w-4 h-4 rounded-sm shrink-0"
                        loading="lazy"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <span className="text-caption text-ink-secondary truncate">{runner.name}</span>
                    {runner.domain && (
                      <span className="text-tiny text-ink-muted truncate">{runner.domain}</span>
                    )}
                    <span className="ml-auto text-tiny text-ink-muted whitespace-nowrap">No runs yet</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default SelectRunnerModal;
